import { TooltipTrigger } from "@visiblethread/react-components";
import clsx from "clsx";
import DocumentFileName from "components/DocumentFileName";
import Flex from "components/Flex";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeft,
  ChevronsRight,
  MinusIcon,
  PanelRightCloseIcon,
  PanelRightOpenIcon,
  PlusIcon,
  SearchIcon,
  X,
} from "lucide-react";
import { bool, func, string } from "prop-types";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsPanelOpen,
  setZoom,
} from "slices/documentViewer/documentViewerSlice";
import {
  selectCurrentPage,
  selectDocumentZoom,
  selectIsPanelOpen,
  selectTotalPages,
} from "slices/documentViewer/selectors";

import DocumentViewerContext from "../DocumentViewerContext";
import classes from "./Toolbar.module.scss";

const ICON_SIZE = 12;
const ZOOM_BY = 0.25;

const Toolbar = ({
  canToggleNarrow,
  fileName,
  onChangePage,
  onClose,
  onToggleNarrow,
  showFileName,
  showNavigation,
  toolbarClassName,
  ...props
}) => {
  const { searchBy, setSearchBy } = useContext(DocumentViewerContext);
  const isPanelOpen = useSelector(selectIsPanelOpen);
  const currentPage = useSelector(selectCurrentPage);
  const zoom = useSelector(selectDocumentZoom);
  const totalPages = useSelector(selectTotalPages);
  const dispatch = useDispatch();

  const [isNarrow, setIsNarrow] = useState(Boolean(props.isNarrow));
  const isSinglePageDoc = (totalPages || 1) <= 1;
  const isBackDisabled = isSinglePageDoc || currentPage === 1;
  const isNextDisabled = isSinglePageDoc || currentPage === totalPages;

  useEffect(() => {
    if (isNarrow !== Boolean(props?.isNarrow)) {
      setIsNarrow(Boolean(props.isNarrow));
    }
  }, [isNarrow, props.isNarrow]);

  const onClickPrevNextPage =
    (forward = false) =>
    () => {
      let nextPage = currentPage;
      if (forward) {
        nextPage = Math.min(totalPages, nextPage + 1);
      } else {
        nextPage = Math.max(1, nextPage - 1);
      }
      onChangePage(nextPage);
    };

  const onClickTogglePanel = () => {
    dispatch(setIsPanelOpen(!isPanelOpen));
  };

  const onClickPlusMinusZoom =
    (plus = false) =>
    () => {
      let nextZoom = zoom;
      const remainder = nextZoom % ZOOM_BY;

      // if current zoom is custom / from resizing,
      // then reset to the next available zoom level
      if (remainder !== 0) {
        const operator = plus ? Math.ceil : Math.floor;
        nextZoom = operator(nextZoom / ZOOM_BY) * ZOOM_BY;

        dispatch(setZoom(nextZoom));
        return;
      }

      if (plus) {
        nextZoom = Math.min(5, nextZoom + ZOOM_BY);
      } else {
        nextZoom = Math.max(ZOOM_BY, nextZoom - ZOOM_BY);
      }

      dispatch(setZoom(nextZoom));
    };

  const onClickReset = () => {
    dispatch(setZoom(1));
  };

  const onClickToggleNarrow = (e) => {
    e.stopPropagation();
    const newIsNarrow = !isNarrow;

    setIsNarrow(newIsNarrow);
    onToggleNarrow(newIsNarrow);
  };

  const onChangePageInput = (e) => {
    const { value } = e.target;
    onChangePage(Number(value));
  };

  const onChangeSearch = (e) => {
    const { value } = e.target;
    setSearchBy(value);
  };

  const pageGroup = (
    <Flex className={classes["toolbar__group"]}>
      <button
        data-testid="prev-page-btn"
        disabled={isBackDisabled}
        onClick={onClickPrevNextPage(false)}
      >
        <ChevronLeftIcon size={ICON_SIZE} />
      </button>

      <input
        className={classes["toolbar__page_input"]}
        disabled={isSinglePageDoc}
        onChange={onChangePageInput}
        type="number"
        value={currentPage}
      />

      <button
        data-testid="next-page-btn"
        disabled={isNextDisabled}
        onClick={onClickPrevNextPage(true)}
      >
        <ChevronRightIcon size={ICON_SIZE} />
      </button>
    </Flex>
  );

  if (canToggleNarrow && isNarrow) {
    return (
      <Flex
        justifyContent="flex-end"
        className={clsx(classes["toolbar"], toolbarClassName)}
      >
        <DocumentFileName fileName={fileName} />

        {pageGroup}

        <button
          aria-label="Widen Document Preview"
          onClick={onClickToggleNarrow}
        >
          <PanelRightOpenIcon size={16} aria-hidden="true" />
        </button>
      </Flex>
    );
  }

  return (
    <Flex className={clsx(classes["toolbar"], toolbarClassName)}>
      {!isSinglePageDoc && showNavigation && (
        <Flex className={classes["toolbar__group"]}>
          <button data-testid="panel-btn" onClick={onClickTogglePanel}>
            {isPanelOpen ? (
              <TooltipTrigger content="Collapse" placement="top">
                <ChevronsLeft size={ICON_SIZE} />
              </TooltipTrigger>
            ) : (
              <TooltipTrigger content="Expand" placement="top">
                <ChevronsRight size={ICON_SIZE} />
              </TooltipTrigger>
            )}
          </button>
        </Flex>
      )}

      {showFileName && (
        <Flex
          className={clsx(
            classes["toolbar__group"],
            classes["toolbar__group--fileName"]
          )}
        >
          <DocumentFileName fileName={fileName} />
        </Flex>
      )}

      {pageGroup}

      <Flex
        className={clsx(
          classes["toolbar__group"],
          classes["toolbar__group--zoom"]
        )}
      >
        <button data-testid="minus-btn" onClick={onClickPlusMinusZoom(false)}>
          <MinusIcon size={ICON_SIZE} />
        </button>

        <button
          className={clsx(classes["toolbar__group__zoom"], {
            [classes["toolbar__group__zoom--reset"]]: zoom === 1,
          })}
          onClick={onClickReset}
          title="Reset Zoom"
        >
          <SearchIcon size={ICON_SIZE} />
          <span>{Math.round(zoom * 100)}%</span>
        </button>

        <button data-testid="plus-btn" onClick={onClickPlusMinusZoom(true)}>
          <PlusIcon size={ICON_SIZE} />
        </button>
      </Flex>

      <Flex
        className={clsx(
          classes["toolbar__group"],
          classes["toolbar__group--search"]
        )}
      >
        <input
          className={classes["toolbar__search_input"]}
          onChange={onChangeSearch}
          placeholder="Search ..."
          value={searchBy}
        />
      </Flex>

      {canToggleNarrow && (
        <Flex className={clsx(classes["toolbar__group"], classes.toggleNarrow)}>
          <button
            aria-label="Narrow Document Preview"
            onClick={onClickToggleNarrow}
          >
            <PanelRightCloseIcon size={16} aria-hidden="true" />
          </button>
        </Flex>
      )}

      {onClose && (
        <Flex className={classes["toolbar__group__close"]}>
          <button
            data-testid="close-btn"
            className={classes["toolbar__group__closeButton"]}
            onClick={onClose}
            type="button"
          >
            <span className={classes["toolbar__closeText"]}>Close</span>
            <X size={16} aria-hidden="true" />
          </button>
        </Flex>
      )}
    </Flex>
  );
};

Toolbar.propTypes = {
  canToggleNarrow: bool,
  fileName: string,
  isNarrow: bool,
  onChangePage: func,
  onClose: func,
  onToggleNarrow: func,
  showFileName: bool,
  showNavigation: bool,
  toolbarClassName: string,
};

export default Toolbar;
